import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { createBarcodeDetector } from '../../utils/barcodeDetector';

const DETECT_INTERVAL_MS = 150;

function BarcodeScannerDialog({ open, onClose, onScan }) {
  const { t } = useTranslation();
  const [cameraError, setCameraError] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const animFrameRef = useRef(null);
  const lastDetectRef = useRef(0);
  const scannedRef = useRef(false);

  const stopCamera = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      stopCamera();
      setCameraError(null);
      setInitializing(true);
      scannedRef.current = false;
      return;
    }

    let cancelled = false;

    const startScanner = async () => {
      try {
        // Native BarcodeDetector when available, otherwise the WASM ponyfill
        // (loaded lazily here). This awaits the WASM download+init on iOS.
        detectorRef.current = await createBarcodeDetector();
      } catch {
        if (!cancelled) setCameraError(t('barcode.cameraError'));
        return;
      }

      if (cancelled) return;

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
      } catch {
        if (!cancelled) setCameraError(t('barcode.cameraError'));
        return;
      }

      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;

      video.srcObject = stream;
      video.setAttribute('playsinline', '');
      await video.play();
      if (!cancelled) setInitializing(false);

      const detect = async (timestamp) => {
        if (cancelled || scannedRef.current) return;

        if (timestamp - lastDetectRef.current >= DETECT_INTERVAL_MS) {
          lastDetectRef.current = timestamp;
          try {
            const results = await detectorRef.current.detect(video);
            if (results.length > 0 && !scannedRef.current) {
              scannedRef.current = true;
              onScan(results[0].rawValue);
              return;
            }
          } catch {
            // detect() can fail on some frames, just skip
          }
        }

        animFrameRef.current = requestAnimationFrame(detect);
      };

      animFrameRef.current = requestAnimationFrame(detect);
    };

    startScanner();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [open, onScan, stopCamera, t]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        {t('barcode.scanBarcode')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        {cameraError ? (
          <Alert severity="error" sx={{ m: 2, borderRadius: '10px' }}>
            {cameraError}
          </Alert>
        ) : initializing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CircularProgress />
          </Box>
        ) : null}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            display: initializing || cameraError ? 'none' : 'block',
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export default BarcodeScannerDialog;
